import { Toaster } from "react-hot-toast";
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import BrijLoadingIndicator from "./Common/Misc/BrijLoadingIndicator";
import LoginPage from "./Common/Pages/LoginPage";
import StudentDashboard from "./Student/Pages/StudentDashboard";
import OnboardingFlow from "./Parent/OnboardingFlow";
import StudentLesson from "./Student/Pages/StudentLesson";
// import Calendar from "./Teacher/Components/Calendar/Calendar";
import Settings from "./Teacher/Components/Settings/Settings";
import TeacherLayout from "./Teacher/Layout/TeacherLayout";
import EPMakerPage from "./Teacher/Pages/EPMakerPage";
import LessonPlanningPage from "./Teacher/Pages/LessonPlanningPage";
import StudentFeedbackPage from "./Teacher/Pages/StudentFeedbackPage";
import TeacherLesson from "./Teacher/Pages/TeacherLesson";
import ParentDashboard from "./Parent/ParentDashboard";
import { ParentDetails2 } from "./Parent/ParentDetails2";
import { ChildDetails } from "./Parent/ChildDetails";
import { ChildAvatarDetails } from "./Parent/ChildAvatarDetails";
import { ReferralGetter } from "./Parent/ReferralGetter";
import StudentLogin from "./Student/Components/StudentLogin";
import Playground from "./util/Playground";
import SelectLoginPage from "./Common/Pages/SelectLoginPage";
import ChildSelection from "./Parent/ChildSelection";
import { createClient } from "@supabase/supabase-js";
import { useEffect } from "react";
import ResetPasswordPage from "./Common/Pages/ResetPasswordPage";
import Calendar2 from "./Teacher/Components/Calendar/Calendar2";
import { useHMSActions } from "@100mslive/react-sdk";
import { useHMSExceptionHandler } from "./hooks/useHMSExceptionHandler";
import AuthenticationWall from "./Common/Utility/AuthenticationWall";
import PostHogAuthHandler from "./Common/Utility/PostHogAuthHandler";
import curTalkingParty from "./api/talkingParty";
import { supabase } from "./api/supabase/client";

function RedirectHandler() {
  const navigate = useNavigate();
  const hmsActions = useHMSActions();
  const location = useLocation();
  const parsedHash = new URLSearchParams(location.hash.substring(1));

  useEffect(() => {
    const leave = () => {
      hmsActions.leave();
    };
    window.addEventListener("unload", leave);

    return () => {
      window.removeEventListener("unload", leave);
    };
  }, [hmsActions]);

  useEffect(() => {
    if (parsedHash.get("type") === "recovery") {
      navigate(`/password-reset${location.hash}`);
    }
  }, [location.hash]);

  // Fallback: when supabase-js v2 strips the recovery hash before React mounts
  // (race condition that varies by device/storage backend), the hash-based
  // useEffect above never sees `type=recovery`. Supabase still emits a
  // PASSWORD_RECOVERY event for the detected token — use it to navigate.
  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event) => {
      if (event === "PASSWORD_RECOVERY") {
        navigate("/password-reset");
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  return <></>;
}

// SAMPLE: #access_token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJhdWQiOiJhdXRoZW50aWNhdGVkIiwiZXhwIjoxNjU5MDY3OTEwLCJzdWIiOiIxZjViZjQzNi1lMDRkLTRlZTUtYmY5OC0yYTdiMWQyNzU3MjQiLCJlbWFpbCI6InBoaWxpcC5hbmRyZXd3ZWVkZXdhbmdAZ21haWwuY29tIiwicGhvbmUiOiIiLCJhcHBfbWV0YWRhdGEiOnsicHJvdmlkZXIiOiJlbWFpbCIsInByb3ZpZGVycyI6WyJlbWFpbCJdfSwidXNlcl9tZXRhZGF0YSI6e30sInJvbGUiOiJhdXRoZW50aWNhdGVkIn0.ZEmq6C1pTCl8dwdNMXMpS04UZXQF4uHg-7n2KQCHucI&expires_in=3600&refresh_token=JdAh7DQ0nWwvsw6F1GP57w&token_type=bearer&type=recovery

//TODO: Make enter button work for form submission
export default function App() {
  useHMSExceptionHandler();

  return (
    <div className="font-poppins h-screen-mobile w-screen overflow-hidden overscroll-none">
      <PostHogAuthHandler />
      <BrowserRouter>
        <RedirectHandler />
        <Routes>
          {/* ROOT */}
          <Route path="/" element={<SelectLoginPage />} />
          <Route
            path="/email-confirmed"
            element={<SelectLoginPage showSuccessfulEmailConfirmed />}
          />
          {/* PARENT PUBLIC*/}
          <Route path="parent/login" element={<LoginPage type="PARENT" />} />
          <Route path="/password-reset" element={<ResetPasswordPage />} />

          {/* PARENT + STUDENT PRIVATE (W/ AUTHENCIATION WALL)*/}
          <Route element={<AuthenticationWall loginPageRoute="parent/login" />}>
            <Route path="parent/flow" element={<OnboardingFlow />}>
              <Route path="parent_details_2" element={<ParentDetails2 />} />
              <Route path="child_details" element={<ChildDetails />} />
              <Route
                path="child_avatar_details"
                element={<ChildAvatarDetails />}
              />
              <Route path="referral_getter" element={<ReferralGetter />} />
            </Route>
            <Route path="parent/child_selection" element={<ChildSelection />} />
            <Route path="parent/dashboard" element={<ParentDashboard />} />
            {/* STUDENT */}
            <Route path="student/login" element={<StudentLogin />} />
            <Route path="student/dashboard" element={<StudentDashboard />} />
            <Route path="student/lesson" element={<StudentLesson />} />
          </Route>

          {/* TEACHER PUBLIC*/}
          <Route path="teacher/login" element={<LoginPage type="TEACHER" />} />

          {/* TEACHER PRVIATE (W/ AUTHENCIATION WALL)*/}
          <Route
            element={<AuthenticationWall loginPageRoute="teacher/login" />}
          >
            <Route path="teacher" element={<TeacherLayout />}>
              <Route path="calendar" element={<Calendar2 />} />
              <Route path="lesson_planning" element={<LessonPlanningPage />} />
              <Route
                path="student_feedback"
                element={<StudentFeedbackPage />}
              />
              <Route path="settings" element={<Settings />} />
              <Route path="loading" element={<BrijLoadingIndicator />} />
            </Route>
            <Route path="teacher/epmaker" element={<EPMakerPage />} />
            <Route path="teacher/lesson" element={<TeacherLesson />} />
            <Route path="playground" element={<Playground />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster />
    </div>
  );
}